/**
 * ObsessedWithSuccessPage — /obsessed-with-success/
 * Страница подкаста «Obsessed With Success» с каталогом выпусков.
 *
 * Что было. Страница встраивала один плеер плейлиста с адресом
 * `videoseries?list=UULFMorganLegalGroup`. `UULF` + идентификатор канала — приём
 * верный, но подставлен ДЕСКРИПТОР `@MorganLegalGroup`, а не идентификатор.
 * Живая страница показывала «This video is unavailable»: единственное её
 * содержимое не работало.
 *
 * Что теперь. Свой каталог из `podcastEpisodes` — 37 выпусков. Плеер YouTube
 * НЕ загружается, пока посетитель не нажал: карточка показывает превью с
 * i.ytimg.com, и только по клику на её место встаёт рамка. Это заметно больше,
 * чем экономия запросов: до нажатия страница не отдаёт посетителя чужим
 * скриптам вовсе, а тридцать семь одновременных рамок сделали бы её
 * неработоспособной.
 *
 * Почему 37, а не 289. Плейлист `UULF` исключает Shorts, но не короткие обычные
 * загрузки: там 159 роликов до минуты, 92 от одной до пяти и лишь 37 в формате
 * подкаста. Страница называется подкастом и обещает разговоры — ключевые ролики
 * по тридцать секунд этому обещанию противоречат. Разбор состава и поле kind —
 * в `src/data/podcastEpisodes.ts`.
 */
import { useEffect, useMemo, useState } from 'react';
import AttorneyAdvertisingNote from '@/components/AttorneyAdvertisingNote';
import { Youtube, Calendar, ChevronRight, Mic, Play, Search } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';
import SchemaJsonLd from '@/components/SchemaJsonLd';
import { podcastEpisodes, type ChannelVideo } from '@/data/podcastEpisodes';
import { applyPageMeta } from '@/lib/pageMeta';

const SITE = 'https://www.morganlegalny.com';
const YT = 'https://www.youtube.com/@MorganLegalGroup';
/** Плейлист загрузок канала — длинные ролики. Прежний адрес был неверным. */
const PLAYLIST = 'https://www.youtube.com/playlist?list=UULFs1oL8hVtMzbzeyLDPj3P-w';
const metaTitle = 'Obsessed With Success — Podcast | Morgan Legal Group';
const metaDescription =
  'Obsessed With Success is Morgan Legal Group’s podcast and video series on estate planning, probate, elder law, and building a lasting legacy. Watch on YouTube.';

/** Превью 480×360 с чёрными полями; кадрируем до 16:9 через object-cover. */
const thumb = (id: string) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

function EpisodeCard({ ep, eager }: { ep: ChannelVideo; eager?: boolean }) {
  const [playing, setPlaying] = useState(false);
  return (
    <article className="group">
      <div className="relative w-full overflow-hidden border border-navy/10 bg-navy" style={{ aspectRatio: '16 / 9' }}>
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${ep.id}?autoplay=1&rel=0`}
            title={ep.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="absolute inset-0 w-full h-full border-0"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play episode: ${ep.title}`}
            className="absolute inset-0 w-full h-full cursor-pointer"
          >
            <img
              src={thumb(ep.id)}
              alt=""
              loading={eager ? 'eager' : 'lazy'}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            />
            <span className="absolute inset-0 bg-navy/25 group-hover:bg-navy/10 transition-colors duration-300" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex items-center justify-center w-16 h-16 rounded-full bg-gold text-navy shadow-lg transition-transform duration-300 group-hover:scale-110">
                <Play className="w-7 h-7 ml-1" fill="currentColor" />
              </span>
            </span>
            <span className="absolute bottom-2 right-2 bg-navy/85 text-white font-body text-xs px-2 py-1">
              {ep.duration}
            </span>
          </button>
        )}
      </div>
      <h3 className="font-display text-navy text-lg font-bold leading-snug mt-4">
        <a
          href={`https://www.youtube.com/watch?v=${ep.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-gold transition-colors"
        >
          {ep.title}
        </a>
      </h3>
      <AttorneyAdvertisingNote />
    </article>
  );
}

function Episodes() {
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return podcastEpisodes;
    return podcastEpisodes.filter((e) => e.title.toLowerCase().includes(needle));
  }, [q]);

  return (
    <section className="bg-cream py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <div className="section-label mb-4">All Episodes</div>
            <h2 className="font-display text-navy text-3xl lg:text-4xl font-bold leading-tight">
              {podcastEpisodes.length} Conversations
            </h2>
          </div>
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search episodes…"
              aria-label="Search episodes by title"
              className="w-full border border-navy/15 bg-white font-body text-sm text-navy pl-9 pr-3 py-3 focus:outline-none focus:border-gold"
            />
          </div>
        </div>

        {list.length === 0 ? (
          <p className="font-body text-navy/60">
            Nothing matches “{q}”.{' '}
            <button type="button" onClick={() => setQ('')} className="text-gold hover:underline">
              Show all {podcastEpisodes.length} episodes
            </button>
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {list.map((ep, i) => (
              <EpisodeCard key={ep.id} ep={ep} eager={i < 3} />
            ))}
          </div>
        )}

        <p className="text-center font-body text-navy/55 text-sm mt-12">
          New episodes land on YouTube first.{' '}
          <a href={PLAYLIST} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
            Browse the full playlist →
          </a>
        </p>
      </div>
    </section>
  );
}

export default function ObsessedWithSuccessPage() {
  const canonical = `${SITE}/obsessed-with-success/`;
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'PodcastSeries',
      name: 'Obsessed With Success',
      url: canonical,
      description: metaDescription,
      webFeed: YT,
      author: { '@type': 'LegalService', name: 'Morgan Legal Group, P.C.', url: SITE },
    },
    {
      // Перечень выпусков машиночитаемо. Поля uploadDate и description здесь НЕТ
      // намеренно: точной даты у нас пока нет (затравка снята из плейлиста, где
      // показано «3 weeks ago»), а описание не выдумывается. Google без них не
      // даст расширенный результат — это осознанная неполнота, а не упущение;
      // оба поля дозаполнит scripts/fetch-podcast-episodes.mjs, когда появится
      // ключ API. Выдуманная дата обошлась бы дороже отсутствующей.
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Obsessed With Success — episodes',
      numberOfItems: podcastEpisodes.length,
      itemListElement: podcastEpisodes.map((ep, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'VideoObject',
          name: ep.title,
          duration: ep.iso,
          thumbnailUrl: thumb(ep.id),
          embedUrl: `https://www.youtube-nocookie.com/embed/${ep.id}`,
          url: `https://www.youtube.com/watch?v=${ep.id}`,
          ...(ep.uploadDate ? { uploadDate: ep.uploadDate } : {}),
        },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Media & News', item: `${SITE}/publications/` },
        { '@type': 'ListItem', position: 3, name: 'Obsessed With Success', item: canonical },
      ],
    },
  ];

  useEffect(() => {
    if (typeof document !== 'undefined') {
      applyPageMeta({ title: metaTitle, description: metaDescription, url: canonical });
    }
  }, [canonical]);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaJsonLd schema={schema} />
      <Navbar />
      <main>
        <section className="relative bg-navy pt-28 lg:pt-32 pb-16 lg:pb-20 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
            <span
              className="absolute bottom-0 -right-8 font-display text-[9rem] lg:text-[14rem] font-bold text-white leading-none whitespace-nowrap"
              style={{ opacity: 0.03 }}
            >
              PODCAST
            </span>
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="Breadcrumb" className="mb-6 font-body text-xs text-white/50 tracking-wide flex items-center gap-1">
              <a href="/" className="hover:text-gold transition-colors">Home</a>
              <ChevronRight className="w-3 h-3 text-gold/60" />
              <a href="/publications/" className="hover:text-gold transition-colors">Media &amp; News</a>
              <ChevronRight className="w-3 h-3 text-gold/60" />
              <span className="text-white/80">Obsessed With Success</span>
            </nav>
            <div className="section-label mb-5 text-gold">
              <Mic className="w-4 h-4 inline-block mr-2 align-middle" />
              The Morgan Legal Group Podcast
            </div>
            <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6">
              Obsessed With <span className="text-gold italic">Success</span>
            </h1>
            <p className="font-body text-white/85 text-lg leading-relaxed mb-8 max-w-2xl">
              Conversations on estate planning, probate, elder law, and building a legacy that lasts — practical
              insights from the attorneys at Morgan Legal Group and the people who inspire them. New episodes are
              published on our YouTube channel.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={YT} target="_blank" rel="noopener noreferrer" className="btn-gold">
                <Youtube className="w-4 h-4" />
                Watch on YouTube
              </a>
              <a href="/appointment/" className="btn-ghost-gold">
                <Calendar className="w-4 h-4" />
                Schedule a Consultation
              </a>
            </div>
          </div>
        </section>

        <Episodes />
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
